import { useMemo } from 'react'
import useFetchAllPages from '@/hooks/useFetchAllPages'
import { useGetDocumentRowValues } from '@/service-library/hooks/document-row-values'
import { useGetDocumentRows } from '@/service-library/hooks/document-rows'
import { useGetDocuments } from '@/service-library/hooks/documents'
import { useGetPOLineItemMatches } from '@/service-library/hooks/po-matches'
import { useGetProjectGridFields } from '@/service-library/hooks/project-grid-fields'
import { useGetWorkflowStates } from '@/service-library/hooks/workflow-states'
import {
  buildRelatedInvoices,
  RELATED_INVOICE_HEADER_INFO_TYPE_IDS,
  RELATED_INVOICE_LINE_INFO_TYPE_IDS,
} from '@/utils/related-invoices'
import { useSelectedWorkflowContext } from '@/components/workflows/SelectedWorkflowProvider'

// Page sizes for the reads that can run long. Every page is fetched, since a
// missing page would silently drop invoices or values.
const MATCHES_LIMIT = '1000'
const LINE_VALUES_LIMIT = '1000'
const LINE_ROWS_LIMIT = '1000'
const FIELDS_LIMIT = '1000'

const joinIds = (ids: (string | undefined)[]) =>
  [...new Set(ids.filter((id): id is string => !!id))].toSorted().join(',')

type UseRelatedInvoicesOptions = {
  /** The focused invoice — left out of the results. */
  documentId: string
  /** The POs the focused invoice has line item matches on. */
  poIds: string[]
}

/**
 * Other invoices with line items matched to any of `poIds`, with the header
 * values and line ties their cards show. Each invoice is read with the fields
 * of its own project, which can differ from the current one.
 */
export default function useRelatedInvoices({
  documentId,
  poIds,
}: UseRelatedInvoicesOptions) {
  const { selectedWorkflow } = useSelectedWorkflowContext()

  const poIdsFilter = poIds.join(',')

  const matchesQuery = useGetPOLineItemMatches({
    filters: {
      limit: MATCHES_LIMIT,
      po_id__in: poIdsFilter,
      'document_row__document_id!': documentId,
      // The row is what says which invoice a match belongs to.
      fields__include: 'match_reasons_ids,document_row',
    },
    enabled: poIds.length > 0,
    // Keep the cards on screen while a new match on this invoice changes the PO list.
    placeholderData: (previousData) => previousData,
  })
  useFetchAllPages(matchesQuery)
  const { poLineItemMatches } = matchesQuery

  // Sorted so the same set of ids always produces the same cache key.
  const relatedDocumentIds = useMemo(
    () =>
      [
        ...new Set(
          poLineItemMatches
            .map((match) => match.document_row?.document_id)
            .filter((id): id is string => !!id && id !== documentId),
        ),
      ].toSorted(),
    [poLineItemMatches, documentId],
  )
  const documentIdsFilter = relatedDocumentIds.join(',')
  const hasRelated = poIds.length > 0 && relatedDocumentIds.length > 0

  const { documents, isLoading: documentsLoading } = useGetDocuments({
    // The state ids are left out of a document by default, and the card's state
    // chip is built from them.
    filters: {
      id__in: documentIdsFilter,
      limit: `${relatedDocumentIds.length}`,
      fields__include: 'workflow_states_ids',
    },
    enabled: hasRelated,
    placeholderData: (previousData) => previousData,
  })

  const projectIdsFilter = [
    ...new Set(documents.map((document) => document.project_id)),
  ]
    .toSorted()
    .join(',')

  // Each project has its own field and grid ids, so the fields for each value a
  // card shows are found by info type in every related invoice's project.
  const fieldsQuery = useGetProjectGridFields({
    filters: {
      project_id__in: projectIdsFilter,
      info_type_id__in: [
        ...RELATED_INVOICE_HEADER_INFO_TYPE_IDS,
        ...RELATED_INVOICE_LINE_INFO_TYPE_IDS,
      ].join(','),
      limit: FIELDS_LIMIT,
    },
    enabled: hasRelated && !!projectIdsFilter,
    placeholderData: (previousData) => previousData,
  })
  useFetchAllPages(fieldsQuery)
  const { projectGridFields: fields } = fieldsQuery

  const {
    baseGridIdsFilter,
    lineGridIdsFilter,
    headerFieldIdsFilter,
    lineFieldIdsFilter,
  } = useMemo(() => {
    const fieldsOf = (infoTypeIds: string[]) =>
      fields.filter(
        (field) =>
          !!field.info_type_id && infoTypeIds.includes(field.info_type_id),
      )
    const headerFields = fieldsOf(RELATED_INVOICE_HEADER_INFO_TYPE_IDS)
    const lineFields = fieldsOf(RELATED_INVOICE_LINE_INFO_TYPE_IDS)
    return {
      baseGridIdsFilter: joinIds(
        headerFields.map(({ project_grid_id }) => project_grid_id),
      ),
      lineGridIdsFilter: joinIds(
        lineFields.map(({ project_grid_id }) => project_grid_id),
      ),
      headerFieldIdsFilter: joinIds(headerFields.map(({ id }) => id)),
      lineFieldIdsFilter: joinIds(lineFields.map(({ id }) => id)),
    }
  }, [fields])

  // Wait for every page of fields, so the reads below run once with every
  // project's ids.
  const fieldsReady = hasRelated && !fieldsQuery.hasNextPage

  // A header value only names the row it sits on, so these rows are what map
  // a value back to its invoice.
  const { documentRows: baseRows, isLoading: baseRowsLoading } =
    useGetDocumentRows({
      filters: {
        document_id__in: documentIdsFilter,
        project_grid_id__in: baseGridIdsFilter,
        limit: `${relatedDocumentIds.length}`,
      },
      enabled: fieldsReady && !!baseGridIdsFilter && !!headerFieldIdsFilter,
      placeholderData: (previousData) => previousData,
    })

  const { documentRowValues: headerValues, isLoading: headerValuesLoading } =
    useGetDocumentRowValues({
      filters: {
        document_row__document_id__in: documentIdsFilter,
        document_row__project_grid_id__in: baseGridIdsFilter,
        project_grid_field__id__in: headerFieldIdsFilter,
        limit: `${relatedDocumentIds.length * 3}`,
      },
      enabled: fieldsReady && !!baseGridIdsFilter && !!headerFieldIdsFilter,
      placeholderData: (previousData) => previousData,
    })

  // Every line row, so an expanded card can list the invoice's untied lines too.
  const lineRowsQuery = useGetDocumentRows({
    filters: {
      document_id__in: documentIdsFilter,
      project_grid_id__in: lineGridIdsFilter,
      limit: LINE_ROWS_LIMIT,
    },
    enabled: fieldsReady && !!lineGridIdsFilter,
    placeholderData: (previousData) => previousData,
  })
  useFetchAllPages(lineRowsQuery)
  const { documentRows: lineRows } = lineRowsQuery

  const lineValuesQuery = useGetDocumentRowValues({
    filters: {
      document_row__document_id__in: documentIdsFilter,
      document_row__project_grid_id__in: lineGridIdsFilter,
      project_grid_field__id__in: lineFieldIdsFilter,
      limit: LINE_VALUES_LIMIT,
    },
    enabled: fieldsReady && !!lineGridIdsFilter && !!lineFieldIdsFilter,
    placeholderData: (previousData) => previousData,
  })
  useFetchAllPages(lineValuesQuery)
  const { documentRowValues: lineValues } = lineValuesQuery

  // A related invoice can sit in another workflow than the one selected here,
  // so its state is looked up by id rather than from the selected workflow.
  const workflowStateIdsFilter = [
    ...new Set(
      documents.flatMap((document) => document.workflow_states_ids ?? []),
    ),
  ]
    .toSorted()
    .join(',')
  const {
    workflowStates: relatedWorkflowStates,
    isLoading: workflowStatesLoading,
  } = useGetWorkflowStates({
    filters: {
      id__in: workflowStateIdsFilter,
      limit: '1000',
      fields__only: 'id,name,color,workflow_id',
    },
    enabled: hasRelated && !!workflowStateIdsFilter,
    placeholderData: (previousData) => previousData,
  })
  // A document has a state in each of its workflows. Listing the selected
  // workflow's states first makes that the one a card shows when it has one.
  const workflowStates = useMemo(
    () => [
      ...(selectedWorkflow?.workflow_states ?? []),
      ...relatedWorkflowStates,
    ],
    [selectedWorkflow?.workflow_states, relatedWorkflowStates],
  )

  const relatedInvoices = useMemo(
    () =>
      hasRelated
        ? buildRelatedInvoices({
            matches: poLineItemMatches,
            documents,
            baseRows,
            lineRows,
            rowValues: [...headerValues, ...lineValues],
            fields,
            workflowStates,
          })
        : [],
    [
      hasRelated,
      poLineItemMatches,
      documents,
      baseRows,
      lineRows,
      headerValues,
      lineValues,
      fields,
      workflowStates,
    ],
  )

  // Still on a first load, or still paging through a read that has to be
  // complete. Placeholder data keeps this false while a later change refetches.
  const isLoading =
    matchesQuery.isLoading ||
    matchesQuery.hasNextPage ||
    documentsLoading ||
    fieldsQuery.isLoading ||
    fieldsQuery.hasNextPage ||
    baseRowsLoading ||
    headerValuesLoading ||
    lineRowsQuery.isLoading ||
    lineRowsQuery.hasNextPage ||
    lineValuesQuery.isLoading ||
    lineValuesQuery.hasNextPage ||
    workflowStatesLoading

  return { relatedInvoices, isLoading }
}
